import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpHandler, HttpRequest, HttpEvent } from '@angular/common/http';
import { Observable, of, switchMap, throwError } from 'rxjs';
import { environment } from '@env/environment';
import { AuthTokenStorageService } from '../auth-token-storage.service';
import { AuthService } from '../auth.service';
import { AuthTokenService } from '../auth-token.service';

const BEARER_PREFIX = 'Bearer ';

@Injectable({
  providedIn: 'root'
})
export class AuthHeaderInterceptor implements HttpInterceptor {

  constructor(
    private _authService: AuthService,
    private _authTokenStorageService: AuthTokenStorageService,
    private _authTokenService: AuthTokenService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler) : Observable<HttpEvent<any>> {
    // endpoint from login page
    const isUnportectedEndpoint = Object
      .values(environment.api.services)
      .some(service => typeof service === 'string' && req.url.includes(service));
    if (isUnportectedEndpoint) {
      return next.handle(req);
    }

    const tokens = this._authService.authorizationTokens;
    if (tokens === null) {
      this._authTokenService.removeTokensAndOpenSessionDialog('session_expired') 
      throw throwError('Authorization tokens not available');
    }

    const isAccessTokenExpired: boolean | null = this._authTokenService.checkTokenExpiration();
    // if access token is not expired
    // if end point is refresh token ignore calling the api - hence to looping the interceptor
    // Refresh token API is the only protected API that still needs an access token even if it is expired
    if (!isAccessTokenExpired || req.url.includes(environment.api.services.authenticationRefreshToken.url) ) {
      // add access to token to req header
      req = req.clone({
        url:  req.url,
        setHeaders: {
          Authorization: BEARER_PREFIX + tokens.accessToken
        }
      });
      return next.handle(req)
    }

    const isRefreshTokenExpired: boolean = this._authTokenService.checkRefreshTokenExpiration()
    // if refresh token expired
    if(isRefreshTokenExpired) {
      this._authTokenService.removeTokensAndOpenSessionDialog('session_expired') 
      throw throwError('Refresh Token Expired')
    }

    // ************ Refresh Token Not Expired And Access Token Expired *****************
    // constructing a pipe in order to manage async methods
    return of().pipe(
      switchMap(() => {
        return this._authService.refreshTokenLocalStorageUpdate()
      }),
      switchMap(() => {
        const newAccessToken = this._authTokenStorageService.getAccessToken()
        // add new access token to req header
        req = req.clone({
          url:  req.url,
          setHeaders: {
            Authorization: BEARER_PREFIX + newAccessToken
          }
        });
        return next.handle(req)
      })
    );
  }
}